import * as Array from 'effect/Array';
import * as P from 'effect/Predicate';
import { MultiPolygon, Point, Polygon } from 'geojson';

/**
 * Point coordinates is an array of two numbers: [longitude, latitude].
 * [100.0, 0.0]
 * https://datatracker.ietf.org/doc/html/rfc7946#appendix-A.1
 */
export const isPointCoordinates = (
  coordinates: unknown,
): coordinates is Point['coordinates'] =>
  Array.isArray(coordinates) &&
  coordinates.length === 2 &&
  typeof coordinates[0] === 'number' &&
  typeof coordinates[1] === 'number';

export function toPointGeometry(coordinates: unknown): Point | undefined {
  if (!isPointCoordinates(coordinates)) {
    return undefined;
  }
  return {
    type: 'Point',
    coordinates,
  };
}

export function toPolygonOrMultiPolygonGeometry(
  coordinates: unknown,
): Polygon | MultiPolygon | undefined {
  if (isMultiPolygonCoordinates(coordinates)) {
    return {
      type: 'MultiPolygon',
      coordinates,
    };
  }
  if (isPolygonCoordinates(coordinates)) {
    return {
      type: 'Polygon',
      coordinates,
    };
  }
  return undefined;
}

/**
 * MultiPolygon geometry is an array of Polygons (= array of LatLng coordinates).
 * https://datatracker.ietf.org/doc/html/rfc7946#appendix-A.6
 * [
 *   // Polygon without hole
 *   [[[0, 0], [10, 0], [0, 10], [0, 0]]],
 *   // Polygon with hole
 *   [
 *     [[0, 0], [10, 0], [0, 10], [0, 0]],
 *     [[2, 2], [8, 2], [2, 8], [2, 2]]
 *   ]
 * ]
 */
export const isMultiPolygonCoordinates = (
  coordinates: unknown,
): coordinates is MultiPolygon['coordinates'] => {
  if (!isArrayWithValues(coordinates)) {
    return false;
  }
  const firstPolygon: unknown = coordinates[0];
  if (!isArrayWithValues(firstPolygon)) {
    return false;
  }
  const firstRing: unknown = firstPolygon[0];
  if (!isArrayWithValues(firstRing)) {
    return false;
  }
  return Array.isArray(firstRing[0]);
};

/**
 * Polygon geometry is an array of exterior ring and eventually hole(s).
 * https://datatracker.ietf.org/doc/html/rfc7946#appendix-A.3
 * [
 *   // Ring
 *   [[0, 0], [10, 0], [0, 10], [0, 0]],
 *   // Hole
 *   [[2, 2], [8, 2], [2, 8], [2, 2]]
 * ]
 */
export const isPolygonCoordinates = (
  coordinates: unknown,
): coordinates is Polygon['coordinates'] => {
  if (!isArrayWithValues(coordinates)) {
    return false;
  }
  const firstRing: unknown = coordinates[0];
  if (!isArrayWithValues(firstRing)) {
    return false;
  }
  const firstPosition: unknown = firstRing[0];
  return Array.isArray(firstPosition) && !Array.isArray(firstPosition[0]);
};

const isArrayWithValues = P.compose(
  Array.isArray as P.Refinement<unknown, unknown[]>,
  Array.isNonEmptyArray,
);
